import { PencilIcon, PlusIcon, Squares2X2Icon, XMarkIcon } from '@heroicons/react/24/solid';
import { Button, Card, CardBody, CardHeader } from '@heroui/react';
import { type FC, useEffect, useState } from 'react';
import { DescriptionFormDialog } from '@/app/components/description-form-dialog.tsx';
import { NoData } from '@/app/components/no-data.tsx';
import { usePageLoading } from '@/app/components/page-loading.tsx';
import toast from '@/app/toast.ts';
import type { AccountModel, StaffMemberCategory } from '@/app/types/account.type.ts';
import {
  useDeleteStaffMemberCategoryMutation,
  usePostStaffMemberCategoryMutation,
  usePutStaffMemberCategoryMutation,
} from '@/slices/account-api-slice.ts';

export interface ManageStaffMemberCategoriesProps {
  account: AccountModel;
}

const ManageStaffMemberCategories: FC<ManageStaffMemberCategoriesProps> = ({ account }) => {
  const [post, { isLoading: postLoading, isSuccess: postSuccess, isError: postError }] =
    usePostStaffMemberCategoryMutation();
  const [put, { isLoading: putLoading, isSuccess: putSuccess, isError: putError }] =
    usePutStaffMemberCategoryMutation();
  const [
    deleteCategory,
    { isLoading: deleteLoading, isSuccess: deleteSuccess, isError: deleteError },
  ] = useDeleteStaffMemberCategoryMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [selectedValue, setSelectedValue] = useState(null);

  useEffect(() => {
    let action = '';
    if (postSuccess || postError) {
      action = 'added';
    } else if (putSuccess || putError) {
      action = 'updated';
    } else if (deleteSuccess || deleteError) {
      action = 'deleted';
    }

    if (postSuccess || putSuccess || deleteSuccess) {
      toast.success(`Staff Category successfully ${action}.`);
    }
    if (postError || putError || deleteError) {
      toast.error(`An error has occurred. Staff Category could not be ${action}.`);
    }
  }, [postSuccess, postError, putSuccess, putError, deleteSuccess, deleteError]);

  const handleDelete = (value: StaffMemberCategory) => {
    deleteCategory({ accountKey: account.key, id: value.id });
  };

  const handleOnClose = (value: string) => {
    if (value && selectedValue?.id) {
      put({
        id: selectedValue.id,
        description: value,
        accountId: selectedValue.accountId,
      } as any);
    } else if (value && !selectedValue?.id) {
      post({
        id: 0,
        description: value,
        accountId: account.id,
      } as any);
    }

    setIsOpen(false);
  };

  const openEditDialog = (value: StaffMemberCategory) => {
    setTitle('Edit Staff Member Category');
    setSelectedValue(value);
    setIsOpen(true);
  };

  const openAddDialog = () => {
    setTitle('Add Staff Member Category');
    setSelectedValue(null);
    setIsOpen(true);
  };

  const { showPageLoading, hidePageLoading } = usePageLoading();

  useEffect(() => {
    putLoading || postLoading || deleteLoading
      ? showPageLoading('edit-staff-categories')
      : hidePageLoading('edit-staff-categories');
  }, [putLoading, postLoading, deleteLoading]);

  const staffCategories = account?.staffMemberCategories ?? [];

  return (
    <>
      <DescriptionFormDialog
        placeHolder='Physicians, Nurses, Administrators...'
        title={title}
        initialValue={selectedValue?.description ?? ''}
        label='Description'
        onClose={handleOnClose}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
      <Card>
        <CardHeader className='flex justify-between items-center px-4 pt-4'>
          <div className='flex items-center gap-2'>
            <Squares2X2Icon className='size-5' aria-hidden='true' />
            <h2 className='font-semibold'>Staff Categories</h2>
            <div className='h-6 w-6 flex items-center justify-center font-semibold rounded-full bg-default-200 text-default-500 text-sm'>
              {staffCategories.length}
            </div>
          </div>
          <Button color='primary' variant='bordered' onPress={openAddDialog}>
            <PlusIcon className='size-5' />
            Add Staff Category
          </Button>
        </CardHeader>
        <CardBody className='px-4 pb-4'>
          <div className='flex-col'>
            {!staffCategories.length && <NoData message='No staff categories found' />}
            {staffCategories.map(category => {
              return (
                <div
                  className='flex items-center px-4 py-2 listGroupItem bg-default-50'
                  key={category.id}
                >
                  <div className='flex-1'>{category.description}</div>
                  <div className='flex gap-2'>
                    <Button
                      isIconOnly
                      size='sm'
                      variant='flat'
                      color='primary'
                      onPress={() => openEditDialog(category)}
                    >
                      <PencilIcon className='size-4' />
                    </Button>
                    <Button
                      isIconOnly
                      size='sm'
                      color='danger'
                      variant='flat'
                      onPress={() => handleDelete(category)}
                    >
                      <XMarkIcon className='size-4' />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default ManageStaffMemberCategories;
